import { memo, useEffect, useState } from 'react';
import AgProfilePage from '../../../stories/Components/PageTemplates/AgProfilePage';
import { AgTabsDataProps } from '../../../stories/Components/AgTab';
import defaultAvatar from '../../../stories/Components/AgAvatar/defaultAvatar.svg';
import AgTypography from '../../../stories/Styles/AgTypography';
import AgInfoCard from '../../../stories/Components/Cards/AgInfoCard';
import { useIntl } from 'react-intl';
import PersonalDetails from '../PersonalDetails';
import Settings from '../Settings';
import ProfileDetails from '../ProfileDetails';
import { useNavigate } from 'react-router-dom';
import {
  ACCOUNT_TYPE_ID,
  CustomFieldColumnType,
  CustomFieldItemType,
  MANDATORY_LEVEL,
  MEMBER_EDIT_PERMISSION,
  MEMBER_READ_PERMISSION,
  ORG_IMAGE_TYPE_ID,
  ROOT_PATHS,
  SUCCESS_STATUS
} from '../../../Constants';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  ApiColumnType,
  ApiCustomField, ApiEnumGroup,
  ApiIdentityOut, ApiItemData, ApiMandatoryLevel, ApiOrganizationProfile,
  ApiUserInfo
} from '@agunity/api-v4/build/data-contracts';
import AgButton from '../../../stories/Components/AgButton';
import AgModal from '../../../stories/AgModal';
import {
  selectIsOpenViewAvatar, setEmailExists,
  setIsDirty_Staff_Personal,
  setIsOpenUnsavedModal,
  setIsOpenViewAvatar, setIsToastOpen_navFromAddStaff, setStaff_EnumList,
  setStaff_Redirect_URL, setStaff_TagList, setNameCheck, setSelectedRoles
} from '../../../redux/slices/staff';
import { cloneDeep, isEqual } from 'lodash';
import AgAvatar from '../../../stories/Components/AgAvatar';
import AgProgressBar from '../../../stories/AgProgressBar';
import { isEmpty } from 'lodash';
import { addNewStaff, getCustomFieldByTagAndFunction, getEnumList } from '../../Users/redux';
import { getImage, getOrganization, saveImage } from '../../../redux/slices/organizations';
import SummaryCardPage from './SummaryCardPage';
import { ApiCreatePersonRequest, ApiImageWrapper, ApiItemTypes } from '@agunity/api-v4';
import AgToast from '../../../stories/Components/AgToast';
import { selectOrgTree } from '../../../redux/slices/global';
import { findNodeById, RenderTree } from '../../../stories/Utils/TreeUtils';
import AgNoPermission from '../../../components/AgNoPermission';

export interface HasOnBlurProps {
  firstName: boolean;
  city: boolean;
  gender: boolean;
  dateOfBirth: boolean;
  country: boolean;
  address1: boolean;
  latitude: boolean;
  longitude: boolean;
  language: boolean;
  middleName: boolean;
  lastName: boolean;
  notes: boolean;
  address2: boolean;
  address3: boolean;
  province: boolean;
  postCode: boolean;
  altitude: boolean;
  phone: boolean;
  email: boolean;
  [key: string]: boolean;
}

export interface PersonalDetailsDataProps {
  key: string;
  fieldType: ApiColumnType | undefined;
  itemType: ApiItemTypes | undefined;
  mandatoryLevel: ApiMandatoryLevel | undefined;
  value: string
}

export interface ValidationListPersonalProps {
  firstName: string[];
  middleName: string[];
  lastName: string[];
  dateOfBirth: string[];
  city: string[];
  gender: string[];
  notes: string[];
  language: string[];
  address1: string[];
  address2: string[];
  address3: string[];
  country: string[];
  province: string[];
  postCode: string[];
  latitude: string[];
  longitude: string[];
  altitude: string[];
  phone: string[];
  email: string[];
}

interface ExtendedRenderTree extends RenderTree {
  hasCh?: boolean;
}

const AddStaff = memo(() => {
  const messages = useIntl().formatMessage;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const helpLink = useAppSelector((state) => (state.language.helpLink));
  const user = useAppSelector(state => state.global.users);
  const permissions = (user?.length && user[0].permissions) || [];

  const orgTree = useAppSelector(selectOrgTree);
  const currentOrgId = useAppSelector((state) => state.orgs.searchOrgId);
  const currentOrg = findNodeById(orgTree as ExtendedRenderTree, currentOrgId as string);
  const orgInfo = {
    id: currentOrgId,
    name: currentOrg?.name,
  };
  const hasTagList = useAppSelector(state => state.staff.tagList);
  const [tagList, setTagList] = useState<ApiCustomField[]>(hasTagList);
  const hasEnumList = useAppSelector(state => state.staff.enumList);
  const [enumList, setEnumList] = useState<ApiEnumGroup[]>(hasEnumList);
  const isOpenUnsavedModal = useAppSelector(state => state.staff.isOpenUnsavedModal);
  const redirect_URL = useAppSelector(state => state.staff.staff_Redirect_URL);
  const isOpenViewAvatar = useAppSelector(selectIsOpenViewAvatar);
  const newStaffFuncAndRolesId = useAppSelector(state => state.staff.addNewStaffFuncAndRoles);
  const allRoles = useAppSelector(state => state.staff.allRolesUnderOrg);
  const editedSelectedRoles = useAppSelector(state => state.staff.selectedRoles);
  // 23 is permission number for editing, 11 is for reading

  const [staffData, setStaffData] = useState<ApiUserInfo[]>([{ roles: [] }]);
  const [orgData, setOrgData] = useState<ApiOrganizationProfile[]>([
    {
      name: orgInfo?.name ?? '',
      id: Number(orgInfo?.id),
      activeStatus: 0,
      data: []
    }
  ]);
  const [originalOrgData, setOriginalOrgData] = useState<ApiOrganizationProfile[]>();
  const readAccess = [MEMBER_READ_PERMISSION, MEMBER_EDIT_PERMISSION].some(num => permissions.includes(num));
  const editAccess = permissions.includes(MEMBER_EDIT_PERMISSION);
  const [personalDetails, setPersonalDetails] = useState<ApiIdentityOut | undefined>({
    firstName: '',
    dateOfBirth: '',
    gender: '',
    activeStatus: 0,
    address: { countryId: 0 },
    data: [],
    locale: ''
  });
  const [originalPersonalDetails, setOriginalPersonalDetails] = useState<ApiIdentityOut>();
  const isDirty = !isEqual(personalDetails, originalPersonalDetails) || !isEqual(orgData, originalOrgData);
  if (isDirty) {
    dispatch(setIsDirty_Staff_Personal(true));
  } else {
    dispatch(setIsDirty_Staff_Personal(false));
  }

  // if there is no DOB, or DOB is in the future, then age is unknown
  const [ageUnknown, setAgeUnknown] = useState<boolean>(false);
  const [hasPersonalDetailsError, setHasPersonalDetailsError] = useState<boolean>(false);
  const [hasStaffProfileError, setHasStaffProfileError] = useState<boolean>(false);
  const [selectedCard, setSelectedCard] = useState<number>(0);
  const [openDirtyModal, setOpenDirtyModal] = useState<boolean>(false);
  const [profileIndex, setProfileIndex] = useState<number>(0);
  const [imageURL, setImageURL] = useState<string>('');
  const [progressPercentage, setProgressPercentage] = useState<number>(0);
  const [numberOfPersonalCompleted, setNumberOfPersonalCompleted] = useState<number>(0);
  const [numberOfStaffCompleted, setNumberOfStaffCompleted] = useState<number>(0);
  const [numberOfPersonalAll, setNumberOfPersonalAll] = useState<number>(0);
  const [numberOfStaffAll, setNumberOfStaffAll] = useState<number>(0);
  const [haveSeenSetUpProfile, setHaveSeenSetUpProfile] = useState<boolean>(false);
  const [haveSeenSummary, setHaveSeenSummary] = useState<boolean>(false);
  const [missingMandatoryFields_personal, setMissingMandatoryFields_personal] = useState<string[]>([]);
  const [missingMandatoryFields_staff, setMissingMandatoryFields_staff] = useState<string[]>([]);
  const [otherErrorsExceptForMandatoryFields, setOtherErrorsExceptForMandatoryFields] = useState<string[]>([]);
  const [showErrorToast, setShowErrorToast] = useState<boolean>(false);
  const [showErrorToastMessage, setShowErrorToastMessage] = useState<string>('');
  const [isAddNewButtonLoading, setIsAddNewButtonLoading] = useState<boolean>(false);
  const [hasOnBlur, setHasOnBlur] = useState<HasOnBlurProps>({
    firstName: false,
    dateOfBirth: false,
    city: false,
    gender: false,
    language: false,
    address1: false,
    country: false,
    latitude: false,
    longitude: false,
    middleName: false,
    lastName: false,
    notes: false,
    address2: false,
    address3: false,
    province: false,
    postCode: false,
    altitude: false,
    phone: false,
    email: false
  });
  const [validationListPersonal, setValidationListPersonal] = useState<ValidationListPersonalProps>(
    {
      firstName: [messages({ id: 'MEMBER_Firstname_CANNOT_EMPTY_CAPTION' })],
      middleName: [],
      lastName: [],
      dateOfBirth: [messages({ id: 'MEMBER_DOB_CAPTION' })],
      province: [],
      notes: [],
      city: [],
      postCode: [],
      gender: [messages({ id: 'MEMBER_GENDER_CAPTION' })],
      language: [messages({ id: 'MEMBER_LANGUAGE_CAPTION' })],
      address1: [],
      address2: [],
      address3: [],
      country: [messages({ id: 'MEMBER_COUNTRY_CAPTION' })],
      latitude: [],
      longitude: [],
      altitude: [],
      phone: [],
      email: []
    }
  );

  const fullName =
    (personalDetails?.firstName ?? '') +
    (personalDetails?.middleName ? ' ' + personalDetails.middleName : '') +
    (personalDetails?.familyName ? ' ' + personalDetails.familyName : '');

  const mandatoryMessagesHandledSeparately: string[] = [
    messages({ id: 'MEMBER_Firstname_CANNOT_EMPTY_CAPTION' }),
    messages({ id: 'MEMBER_DOB_CAPTION' }),
    // messages({ id: 'MEMBER_City_CAPTION' }), // city becomes optional at the moment
    messages({ id: 'MEMBER_GENDER_CAPTION' }),
    messages({ id: 'MEMBER_LANGUAGE_CAPTION' }),
    messages({ id: 'MEMBER_ADDRESS1_CAPTION' }),
    messages({ id: 'MEMBER_COUNTRY_CAPTION' }),
    messages({ id: 'MEMBER_Latitude_CAPTION_MANDATORY' }),
    messages({ id: 'MEMBER_Longitude_CAPTION_MANDATORY' })
  ];

  useEffect(() => {
    if (!personalDetails?.imageId) return;
    const fetch = async () => {
      try {
        if (personalDetails?.imageId) {
          const response = await dispatch(getImage(ORG_IMAGE_TYPE_ID, personalDetails?.imageId));
          setImageURL(response?.data);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personalDetails?.imageId]);
  
  useEffect(()=>{
    const otherErrors = () => {
      let otherErrorList: string[] = [];
      if (!validationListPersonal) return;
      for (const key in validationListPersonal) {
        //@ts-ignore
        validationListPersonal[key].forEach(item => {
          if (!mandatoryMessagesHandledSeparately.includes(item) && !(key.toUpperCase() === 'DATEOFBIRTH' && ageUnknown)){// if age is unknown, then age error caption will be hidden
            otherErrorList.push(item);
          }
        });
      }
      setOtherErrorsExceptForMandatoryFields(otherErrorList);
    };
    otherErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[ageUnknown, validationListPersonal]);

  useEffect(() => { // this hook is to monitor the progress percentage
    let numberOfPersonalMandatoryCompleted = 0;
    let numberOfStaffMandatoryCompleted = 1; // 'staff of' and 'roles' will always be filled
    let numberOfPersonalMandatoryAll = 5; // currently 5 fields are mandatory beside custom fields
    let numberOfStaffMandatoryAll = 2; // 'staff of' and 'roles' would be mandatory
    let missingFields_personal = [];
    let missingFields_staff : string[] = [];
    let onBlurArray: Record<string, boolean> = {};

    isEmpty(personalDetails?.firstName)
      ? missingFields_personal.push(messages({ id: 'FIRSTNAME' }))
      : numberOfPersonalMandatoryCompleted++;
    (isEmpty(personalDetails?.dateOfBirth) && !ageUnknown)
      ? missingFields_personal.push(messages({ id: 'MEMBER_PERSONAL_DETAILS_DATE_OF_BIRTH' }))
      : numberOfPersonalMandatoryCompleted++;
    isEmpty(personalDetails?.gender)
      ? missingFields_personal.push(messages({ id: 'MEMBER_PERSONAL_DETAILS_GENDER' }))
      : numberOfPersonalMandatoryCompleted++;
    isEmpty(personalDetails?.locale)
      ? missingFields_personal.push(messages({ id: 'MEMBER_PERSONAL_DETAILS_LANGUAGE' }))
      : numberOfPersonalMandatoryCompleted++;
    // as per 5045, address line 1 becomes optional. If it returns to mandatory again, plz recover below 3 lines
    // isEmpty(personalDetails?.address.addressLine1)
    //   ? missingFields_personal.push(messages({ id: 'MEMBER_PERSONAL_DETAILS_ADDRESS_1' }))
    //   : numberOfPersonalMandatoryCompleted++;
    // isEmpty(personalDetails?.address.city) // city is optional at the moment
    //   ? missingFields_personal.push(messages({ id: 'MEMBER_PERSONAL_DETAILS_CITY' }))
    //   : numberOfPersonalMandatoryCompleted++;
    personalDetails?.address.countryId
      ? numberOfPersonalMandatoryCompleted++
      : missingFields_personal.push(messages({ id: 'MEMBER_PERSONAL_DETAILS_COUNTRY' }));

    if (personalDetails?.address.location?.X || personalDetails?.address.location?.Y) {
      personalDetails?.address.location?.X
        ? numberOfPersonalMandatoryCompleted++
        : missingFields_personal.push(messages({ id: 'MEMBER_PERSONAL_DETAILS_GPS_LATITUDE' }));
      personalDetails?.address.location?.Y ?
        numberOfPersonalMandatoryCompleted++
        : missingFields_personal.push(messages({ id: 'MEMBER_PERSONAL_DETAILS_GPS_LONGITUDE' }));
      numberOfPersonalMandatoryAll += 2;
    }

    const conditionalPersonalFieldList: number[] = [];
    personalDetails?.data?.forEach(field => {
      // @ts-ignore fieldType cannot be Header
      if (field?.fieldType !== CustomFieldColumnType.Header && field?.mandatoryLevel === MANDATORY_LEVEL.Conditional && !isEmpty(field.value)){
        const parentId = tagList.find(tag => tag.key === field.key)?.parentId;
        parentId && !conditionalPersonalFieldList.includes(parentId) && conditionalPersonalFieldList.push(parentId);
      }
    });
    personalDetails?.data?.forEach(field => {
      // @ts-ignore fieldType cannot be Header
      if (field.fieldType === CustomFieldColumnType.Header) return;
      const fieldInfo = tagList.find(item => item.key === field.key);
      if (fieldInfo && fieldInfo.mandatoryLevel === MANDATORY_LEVEL.Mandatory) {
        numberOfPersonalMandatoryAll++;
        if (hasOnBlur[field.key] === undefined) {
          onBlurArray[field.key] = false;
        }
        isEmpty(field.value)
          ? missingFields_personal.push(field.key)
          : numberOfPersonalMandatoryCompleted++;
      }else if (fieldInfo && fieldInfo.mandatoryLevel === MANDATORY_LEVEL.Conditional){
        if (hasOnBlur[field.key] === undefined) {
          onBlurArray[field.key] = false;
        }
        if (fieldInfo.parentId && conditionalPersonalFieldList.includes(fieldInfo.parentId)){
          numberOfPersonalMandatoryAll++;
          isEmpty(field.value)
            ? missingFields_personal.push(field.key)
            : numberOfPersonalMandatoryCompleted++;
        }
      }
    });

    const conditionalStaffFieldList: number[] = [];

    editedSelectedRoles?.length === 0 ? missingFields_staff.push('Roles') : numberOfStaffMandatoryCompleted++;

    orgData[0].data?.forEach(field => {
      // @ts-ignore fieldType cannot be Header
      if (field?.fieldType !== CustomFieldColumnType.Header && field?.mandatoryLevel === MANDATORY_LEVEL.Conditional && !isEmpty(field.value)){
        const parentId = tagList.find(tag => tag.key === field.key)?.parentId;
        parentId && !conditionalStaffFieldList.includes(parentId) && conditionalStaffFieldList.push(parentId);
      }
    });
    orgData[0].data?.forEach(field => {
      // @ts-ignore fieldType cannot be Header
      if (field?.fieldType === CustomFieldColumnType.Header) return;
      const fieldInfo = tagList.find(item => item.key === field.key);
      if (fieldInfo && fieldInfo.mandatoryLevel === MANDATORY_LEVEL.Mandatory) {
        numberOfStaffMandatoryAll++;
        if (hasOnBlur[field.key] === undefined) {
          onBlurArray[field.key] = false;
        }
        isEmpty(field.value)
          ? missingFields_staff.push(field.key)
          : numberOfStaffMandatoryCompleted++;
      }else if (fieldInfo && fieldInfo.mandatoryLevel === MANDATORY_LEVEL.Conditional){
        if (hasOnBlur[field.key] === undefined) {
          onBlurArray[field.key] = false;
        }
        if (fieldInfo.parentId && conditionalStaffFieldList.includes(fieldInfo.parentId)){
          numberOfStaffMandatoryAll++;
          isEmpty(field.value)
            ? missingFields_staff.push(field.key)
            : numberOfStaffMandatoryCompleted++;
        }
      }
    });

    setMissingMandatoryFields_personal(missingFields_personal);
    setMissingMandatoryFields_staff(missingFields_staff);
    setNumberOfPersonalCompleted(numberOfPersonalMandatoryCompleted);
    setNumberOfStaffCompleted(numberOfStaffMandatoryCompleted);
    setNumberOfPersonalAll(numberOfPersonalMandatoryAll);
    setNumberOfStaffAll(numberOfStaffMandatoryAll);
    setHasOnBlur({ ...hasOnBlur, ...onBlurArray });

    const percentage = Math.floor((numberOfPersonalMandatoryCompleted + numberOfStaffMandatoryCompleted) * 100 / (numberOfPersonalMandatoryAll + numberOfStaffMandatoryAll)) || 0;
    setProgressPercentage(percentage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personalDetails?.locale, personalDetails?.address.addressLine1, personalDetails?.address.countryId, personalDetails?.address.location?.X, personalDetails?.address.location?.Y, personalDetails?.data, personalDetails?.dateOfBirth, personalDetails?.firstName, personalDetails?.gender, ageUnknown, tagList, messages, orgData[0].data, editedSelectedRoles]);
  
  useEffect(()=>{
    const fetchOrgInfo = async () => {
      try {
        const getOrgInfo = await dispatch(getOrganization(Number(orgInfo?.id)));
        if (getOrgInfo.data) {
          setOrgData(prevState => ([{
            ...prevState[0], address: getOrgInfo.data.address, relationships: getOrgInfo.data.relationships, function: getOrgInfo.data.function
          }]));

          // when adding a staff, countryId will be pre-populated once the role & org is chosen
          if (getOrgInfo.data?.address?.countryId) {
            let updatedPersonalDetails = cloneDeep(personalDetails);
            if (updatedPersonalDetails?.address) {
              updatedPersonalDetails.address.countryId = getOrgInfo.data.address?.countryId || 0;
              setPersonalDetails(updatedPersonalDetails);
              // if countryId populated, no error caption pre-defined
              setValidationListPersonal(prevState => ({
                ...prevState,
                country: []
              }));
            }
          }
        }
      }catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    };
    fetchOrgInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, orgInfo?.id]);

  useEffect(() => {
    selectedCard === 1 && setHaveSeenSetUpProfile(true);
    selectedCard === 2 && setHaveSeenSummary(true);
  }, [selectedCard]);
  
  useEffect(()=>{
    if (editedSelectedRoles && editedSelectedRoles?.length) {
      setStaffData([{
        ...staffData[0], roles: editedSelectedRoles.map(role => (
          { id: role, name: allRoles.find(item => item?.id === role)?.name }
        ))
      }]);
    }
    else {
      setStaffData([{
        ...staffData[0], roles: []
      }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[allRoles, editedSelectedRoles]);

  useEffect(() => { // this hook is to update custom field list
    if (!hasTagList?.length) return;
    const convertData: PersonalDetailsDataProps[] = hasTagList.filter(tag => !isEmpty(tag.key)).map(tag => ({
      key: tag.key || '',
      fieldType: tag.fieldType,
      itemType: tag.itemType,
      mandatoryLevel: tag.mandatoryLevel,
      value: ''
    }));
    personalDetails && setPersonalDetails({
      ...personalDetails,
      data: convertData.filter(data => data.itemType === CustomFieldItemType.Identity)
    });
    personalDetails && setOriginalPersonalDetails({
      ...personalDetails,
      data: convertData.filter(data => data.itemType === CustomFieldItemType.Identity)
    });
    orgData && setOrgData([{
      ...orgData[0],
      data: convertData.filter(data => (data.itemType === CustomFieldItemType.Organization || data.itemType === CustomFieldItemType.User))
    }]);
    orgData && setOriginalOrgData([{
      ...orgData[0],
      data: convertData.filter(data => (data.itemType === CustomFieldItemType.Organization || data.itemType === CustomFieldItemType.User))
    }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasTagList]);

  useEffect(() => { // in general the API inside this hook would not be called, unless cache is cleaned
    const fetchTagList = async () => {
      if (tagList?.length || !orgData?.[0]?.function) return;
      try {
        const data = { itemType: [CustomFieldItemType.Organization, CustomFieldItemType.Identity, CustomFieldItemType.User], functionId: orgData?.[0]?.function };
        const fetch = await dispatch(getCustomFieldByTagAndFunction(data));
        if (fetch.data) {
          setTagList(fetch.data);
          dispatch(setStaff_TagList(fetch.data));
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    };

    const fetchCustomDropdownOptions = async () => {
      if (hasEnumList?.length) return;
      try {
        const getStaffProfileData = await dispatch(getEnumList());
        if (getStaffProfileData.data) {
          setEnumList(getStaffProfileData.data);
          dispatch(setStaff_EnumList(getStaffProfileData.data));
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    };
    fetchCustomDropdownOptions();
    fetchTagList();
  }, [dispatch, hasEnumList?.length, orgData, tagList?.length]);

  const handleSetOrgData = (index: number, data: ApiItemData[]) => {
    const updatedOrgData = cloneDeep(orgData);
    updatedOrgData[index].data = data;
    setOrgData(updatedOrgData);
  };

  const handleOrgDataClientId = (index: number, clientId: string) => {
    const updatedOrgData = cloneDeep(orgData);
    updatedOrgData[index].clientId = clientId;
    setOrgData(updatedOrgData);
  };

  const handleAddNewBtn = async ()=>{
    if (otherErrorsExceptForMandatoryFields?.length) {
      const message = otherErrorsExceptForMandatoryFields.join(' ');
      setShowErrorToastMessage(message);
      setShowErrorToast(true);
      // toast with close icon will not be temporary
      // setTimeout(()=>{
      //   setShowErrorToast(false);
      // }, 6000);
      return;
    }
    let updatedPersonalDetails = cloneDeep(personalDetails);
    if (ageUnknown) {
      updatedPersonalDetails = {
        ...updatedPersonalDetails,
        dateOfBirth: '3000-01-01T00:00:00',
      } as ApiIdentityOut;
    }

    const removeEmptyFields = (obj: ApiIdentityOut) => {
      return Object.fromEntries(
        Object.entries(obj).filter(
          ([, value]) => value !== null && value !== undefined && value !== ''
        )
      );
    };

    const roles = editedSelectedRoles?.length ? editedSelectedRoles : [newStaffFuncAndRolesId?.id || 0];
    const data: ApiCreatePersonRequest = {
      orgId: Number(orgInfo?.id),
      roles: roles,
      // @ts-ignore
      identity: { ...removeEmptyFields(updatedPersonalDetails), data: updatedPersonalDetails?.data?.filter(item => item?.fieldType !== CustomFieldColumnType.Header) }
    };

    try {
      setIsAddNewButtonLoading(true);
      const response = await dispatch(addNewStaff(data));
      
      if (response?.status === SUCCESS_STATUS) {
        if (imageURL?.length > 0) {
          const payload = {
            itemId: response?.data?.identityId,
            typeId: ACCOUNT_TYPE_ID,
            base64: imageURL,
          };
          await dispatch(saveImage(payload as ApiImageWrapper));
        }
      }
      // below the code will only be executed if the API call succeeded
      navigate(ROOT_PATHS.STAFF);
      dispatch(setIsOpenUnsavedModal(false));
      dispatch(setIsToastOpen_navFromAddStaff(true));
      dispatch(setIsDirty_Staff_Personal(false));
      dispatch(setStaff_Redirect_URL(''));
      dispatch(setNameCheck({ open: false, message: '' }));
      dispatch(setEmailExists(false));
      dispatch(setSelectedRoles([]));
      // toast with close icon will not be temporary
      // setTimeout(()=>{
      //   dispatch(setIsToastOpen_navFromAddStaff(false));
      // }, 6000);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }finally {
      setIsAddNewButtonLoading(false);
    }
  };

  const onImageUpdate = (base64?: string) => {
    setImageURL(base64 ?? '');
  };

  const viewAvatarModal = (
    <AgModal
      closeIcon
      open={isOpenViewAvatar}
      setOpen={() => {
        dispatch(setIsOpenViewAvatar(false));
      }}
      title={messages({ id: 'MEMBER_AVATAR_POPUP_TITLE' })}
      body={
        <AgTypography>
          <AgTypography
            contentStyle={'AP.Paragraph.Large.Regular'}
            style={{ marginTop: '16px' }}
            message={fullName}
          />
          <AgTypography style={{ marginTop: '15px' }}>
            {imageURL ?
              <img
                src={'data:image/jpeg;base64,' + imageURL}
                alt={messages({ id: 'MEMBER_PERSONAL_DETAILS_PHOTO' })}
                style={{ width: '508px', height: '466px' }}
              /> :
              <AgAvatar
                src={defaultAvatar} alt={messages({ id: 'AVATAR_ALT' })}
                style={{ width: '508px', height: '466px' }}
              />}
          </AgTypography>
        </AgTypography>
      }
    />
  );

  const tabsData: AgTabsDataProps[][] = [ // if there are multiple staff profiles, the sequence of staff data and tabs data should be the same
    [
      {
        label: messages({ id: 'PERSONALDETAILS' }),
        children: <PersonalDetails
          identity={personalDetails}
          setPersonalDetails={setPersonalDetails}
          editAccess={editAccess}
          ageUnknown={ageUnknown}
          setAgeUnknown={setAgeUnknown}
          setHasPersonalDetailsError={setHasPersonalDetailsError}
          hasOnBlur={hasOnBlur}
          setHasOnBlur={setHasOnBlur}
          setValidationListPersonal={setValidationListPersonal}
          validationListPersonal={validationListPersonal}
          imageURL={imageURL}
          onImageUpdate={onImageUpdate}
        />
      },
      {
        label: messages({ id: 'Settings' }),
        children: <Settings
          editAccess={editAccess}
          email={personalDetails?.email || ''}
          setPersonalDetails={setPersonalDetails}
          identity={personalDetails}
        />
      }
    ],
    staffData.map((item, index) => (
      {
        label: messages({ id: 'Profile Details' }),
        children: <ProfileDetails
          key={index}
          editAccess={editAccess}
          data={item}
          tagList={tagList?.filter(tag => {
            return tag.itemType === 1 || tag.itemType === 53;
          })}
          orgData={orgData[index]}
          setOrgData={(data: ApiItemData[]) => {
            handleSetOrgData(index, data);
          }}
          setOrgDataClientId={(clientId: string) => {
            handleOrgDataClientId(index, clientId);
          }}
          identity={personalDetails}
          setHasStaffProfileError={setHasStaffProfileError}
          enumList={enumList}
          hasOnBlur={hasOnBlur}
          setHasOnBlur={setHasOnBlur}
        />
      }
    )),
    [
      {
        children:
          <SummaryCardPage
            setSelectedCard={setSelectedCard}
            personalDetails={personalDetails}
            orgData={orgData[0]}
            staffData={staffData[0]}
            numberOfPersonalMandatoryCompleted={numberOfPersonalCompleted}
            numberOfStaffMandatoryAll={numberOfStaffAll}
            numberOfStaffMandatoryCompleted={numberOfStaffCompleted}
            numberOfPersonalMandatoryAll={numberOfPersonalAll}
            missingMandatoryFields_personal={missingMandatoryFields_personal}
            missingMandatoryFields_staff={missingMandatoryFields_staff}
            ageUnknown={ageUnknown}
            validationListPersonal={validationListPersonal}
            mandatoryMessagesHandledSeparately={mandatoryMessagesHandledSeparately}
            otherErrorsExceptForMandatoryFields={otherErrorsExceptForMandatoryFields}
          />
      }
    ]
  ];

  const infoCardContent = (
    <AgTypography
      style={{
        display: 'grid',
        gap: '16px'
      }}
    >
      <AgTypography contentStyle={'AP.H5.Medium'} message={messages({ id: 'MEMBER_INFO_CARD' })} />
      <AgTypography contentStyle={'AP.H5.Medium'}>
        {messages({ id: 'LEARN_MORE_IN' }, {
          learn: (
            <AgTypography
              component={'span'}
              contentStyle={'AP.H5.Medium'}
              style={{
                cursor: 'pointer'
              }}
              onClick={() => { window.open(helpLink, '_blank'); }}
            >
              {<u>{messages({ id: 'HELP_DOC' })}</u>}
            </AgTypography>
          )
        })}
      </AgTypography>
    </AgTypography>
  );

  const progressBarItems = [
    {
      id: '1',
      title: messages({ id: 'MEMBER_CREATE_ACCOUNT' }),
      subTitle: messages({ id: 'MEMBER_ENTER_PERSONAL_DETAILS' }),
      isCompleted: numberOfPersonalCompleted === numberOfPersonalAll && !otherErrorsExceptForMandatoryFields?.length,
      isActive: selectedCard === 0,
      onClick: () => setSelectedCard(0)
    },
    {
      id: '2',
      title: messages({ id: 'MEMBER_SET_UP_PROFILE' }),
      subTitle: messages({ id: 'MEMBER_SET_UP_PROFILE_SUB' }),
      isCompleted: numberOfStaffCompleted === numberOfStaffAll && haveSeenSetUpProfile, // in case no field is mandatory, user has to see the page
      isActive: selectedCard === 1,
      onClick: () => setSelectedCard(1)
    },
    {
      id: '3',
      title: messages({ id: 'MEMBER_SUMMARY' }),
      subTitle: messages({ id: 'MEMBER_SUMMARY_SUB' }),
      isCompleted: progressPercentage >= 100 && haveSeenSummary,  // in case no field is mandatory, user has to see the page
      isActive: selectedCard === 2,
      onClick: () => setSelectedCard(2)
    }
  ];

  if (!readAccess) <AgNoPermission />;

  return (
    <AgTypography
      style={{
        display: 'grid',
        padding: '16px 32px',
        gap: '8px'
      }}
    >
      <AgProgressBar
        items={progressBarItems}
        percent={progressPercentage}
        showBtn={progressPercentage === 100}
        forward={() => {
          if (selectedCard >= 2) {
            setSelectedCard(2);
            return;
          }
          setSelectedCard(prevState => prevState + 1);
        }}
        backward={() => {
          if (selectedCard <= 0) {
            setSelectedCard(0);
            return;
          }
          setSelectedCard(prevState => prevState - 1);
        }}
        forwardDisabled={selectedCard >= 2}
        backwardDisabled={selectedCard <= 0}
        btnLabel={messages({ id: 'STAFF_PROGRESS_BAR_BUTTON' })}
        btnClick={handleAddNewBtn}
        isAddNewButtonLoading={isAddNewButtonLoading}
        sx={{ maxWidth: '1104px', boxSizing: 'border-box' }}
      />
      <AgTypography
        style={{
          display: 'flex',
          gap: '8px',
          justifyContent: 'start',
          minHeight: '100%'
        }}
      >
        <AgTypography
          style={{
            flex: 1,
            maxWidth: '880px'
          }}
        >
          <AgProfilePage
            // headerHasPersonalDetailsError={hasPersonalDetailsError} // todo waiting for further instruction to display error tab here
            headerHasMemberProfileError={hasStaffProfileError}
            headerTabsData={tabsData}
            headerCardTitle={selectedCard === 0 ? messages({ id: 'PROFILE_ACCOUNT_ID' }) : messages({ id: 'PROFILE_MEMBER_ID' })}
            headerCardInfo={
              selectedCard === 2
                ? messages({ id: 'MEMBER_CARD_INFO' })
                : selectedCard === 1
                  ? messages({ id: 'MEMBER_CARD_INFO_ORIGIN' })
                  : messages({ id: 'MEMBER_CARD_INFO_ACCOUNT' })
            }
            headerCardName={personalDetails?.id ? '#a' + personalDetails?.id?.toString() : ''}
            navHideInfoCard={true}
            navHideAddButton={true}
            navAccountImg={imageURL || defaultAvatar}
            navAccountUserType={personalDetails?.purposeType}
            navAccountName={fullName}
            navAccountID={personalDetails?.id ? '#a' + personalDetails?.id?.toString() : ''}
            navAccountVillage={personalDetails?.address?.city || ''}
            navAccountEmail={personalDetails?.email ?? ''}
            navAccountPhone={personalDetails?.phoneNumber ?? ''}
            navInactive={personalDetails?.activeStatus !== 0} // 0 means active, otherwise, it would be inactive
            navMemberData={orgData || []}
            navOrgData={orgData || []}
            navMemberProfile={personalDetails?.activeStatus}
            selectedCard={selectedCard}
            setSelectedCard={setSelectedCard}
            openDirtyModal={openDirtyModal}
            setOpenDirtyModal={setOpenDirtyModal}
            profileIndex={profileIndex}
            setProfileIndex={setProfileIndex}
            viewAvatarModal={viewAvatarModal}
            showSteps={true}
            style={{
              height: '100%'
            }}
          />
        </AgTypography>
        <AgInfoCard
          title={messages({ id: 'PROFILE_PAGE_NAVI_HEADER1' })}
          closeIcon={false}
          height={'fit-content'}
          children={infoCardContent}
        />
      </AgTypography>

      <AgModal
        closeIcon
        open={isOpenUnsavedModal}
        setOpen={() => {
          dispatch(setIsOpenUnsavedModal(false));
        }}
        title={messages({ id: 'MEMBER_UNSAVED_MODAL_TITLE' })}
        body={
          <AgTypography
            contentStyle={'AP.Paragraph.Large.Regular'}
            style={{ marginTop: '16px', marginBottom: '80px' }}
            message={messages({ id: 'MEMBER_UNSAVED_MODAL_TEXT' })}
          />
        }
        footer={
          <>
            <AgButton
              label={messages({ id: 'CANCEL' })}
              variant='outlined'
              style={{ marginRight: '32px' }}
              onClick={() => {
                dispatch(setIsOpenUnsavedModal(false));
              }}
            />
            <AgButton
              label={messages({ id: 'PROCEED' })}
              onClick={() => {
                dispatch(setIsOpenUnsavedModal(false));
                dispatch(setIsDirty_Staff_Personal(false));
                navigate(redirect_URL, { replace: true });
                dispatch(setStaff_Redirect_URL(''));
                dispatch(setNameCheck({ open: false, message: '' }));
                dispatch(setEmailExists(false));
                dispatch(setSelectedRoles([]));
              }}
            />
          </>
        }
        footerSX={{
          boxSizing: 'border-box'
        }}
      />
      {showErrorToast && <AgToast open={showErrorToast} severity={'error'} message={showErrorToastMessage} onIconClick={()=> {setShowErrorToast(false);}} />}

    </AgTypography>
  );
});

export default AddStaff;